import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaBell,
  FaHeadset,
  FaTimes,
  FaHome,
} from "react-icons/fa";

import PropTypes from "prop-types";
import logo from "../assets/cablenxt.png";
import sbeeLogo from "../assets/sbeelogo.png";


import { MASTER_API, ORDER_API, PURCHASE_API } from "../apiConfig";
import { authHeaders } from "../utils/authHeaders";

const CHANGE_PASSWORD_URL = `${MASTER_API}/auth/change-password`;
// Reuses the same PATCH /user/:userId endpoint the "All users" table's
// inline Edit uses — a user editing their own name is the same operation
// as an admin editing someone else's.
const updateUserUrl = (userId) => `${MASTER_API}/user/${encodeURIComponent(userId)}`;

// Registry of per-module bell notifications. Each entry owns:
//   - match:      which routes this module's alerts apply to
//   - fetchItems: how to load that module's alert list
//   - clickPath / clickState: where the bell navigates on click
// The navbar looks at the current URL, finds the ONE entry whose `match`
// fits, and only fetches/shows that module's alerts. So on an RFQ page
// the bell shows RFQ alerts; on a Sales Order page (once added below) it
// shows Sales alerts instead — never both at once.
//
// To wire up a new module, just add another object here — no other
// changes to the component are needed.
const NOTIFICATION_MODULES = [
  {
    key: "RFQ",
    match: (pathname) => pathname.startsWith("/cable/sales/quotation"),
    label: "RFQ Validity Expired",
    clickPath: "/cable/sales/quotation",
    clickState: { showExpiredOnly: true },
    fetchItems: async () => {
      const res = await fetch(`${ORDER_API}/quotation/summary`, { headers: authHeaders() });
      const data = await res.json();
      const arr = Array.isArray(data) ? data : data.data || [];
      const seen = new Set();
      return arr.filter((r) => {
        if (!r.is_validity_expired || seen.has(r.rfq_id)) return false;
        seen.add(r.rfq_id);
        return true;
      });
    },
  },

  {
    key: "SALES_ORDER",
    match: (pathname) => pathname.startsWith("/cable/sales/sales-order"),
    label: "Sales Order Validity Expired",
    clickPath: "/cable/sales/sales-order",
    clickState: { showExpiredOnly: true },
    fetchItems: async () => {
      const res = await fetch(`${ORDER_API}/sales-order/validity/expired`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      return Array.isArray(data) ? data : data.data || [];
    },
  },

  // Copy this shape for the next module after Sales Order:
  //
  // {
  //   key: "NEXT_MODULE",
  //   match: (pathname) => pathname.startsWith("/cable/next-module"),
  //   label: "Next Module Alerts",
  //   clickPath: "/cable/next-module",
  //   clickState: {},
  //   fetchItems: async () => { ...return an array of alert items... },
  // },

  {
    key: "PURCHASE_REQ",
    match: (pathname) => pathname.startsWith("/cable/procurement/purchase-request"),
    label: "Purchase Requisition Validity Expired",
    clickPath: "/cable/procurement/purchase-request",
    clickState: { showExpiredOnly: true },
    fetchItems: async () => {
      // PURCHASE_API — same base PurchaseRequsition.js itself uses for
      // /purchase-request/* calls (NOT ORDER_API, which RFQ/Sales Order use).
      const res = await fetch(`${PURCHASE_API}/purchase-request/validity/expired`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      return Array.isArray(data) ? data : data.data || [];
    },
  },

  {
    key: "PURCHASE_ORDER",
    // ⚠️ ASSUMED route path — confirm this matches wherever PurchaseOrder.js
    // is actually mounted (same "/cable/procurement/..." convention as
    // Purchase Requisition above).
    match: (pathname) => pathname.startsWith("/cable/procurement/purchase-order"),
    label: "Purchase Order Validity Expired",
    clickPath: "/cable/procurement/purchase-order",
    clickState: { showExpiredOnly: true },
    fetchItems: async () => {
      // PURCHASE_API — PurchaseOrder.js itself calls its endpoints under
      // "/po/*" (e.g. `${PURCHASE_API}/po/purchase-orders`), not
      // "/purchase-order/*" — matches purchaseOrder.routes.js's mount point.
      const res = await fetch(`${PURCHASE_API}/po/validity/expired`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      return Array.isArray(data) ? data : data.data || [];
    },
  },
];

const Navbar = ({ toggleSidebar }) => {
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Whichever module (if any) owns the current route. Recomputed on
  // every navigation so the bell swaps content as soon as the URL changes.
  const activeNotifModule = NOTIFICATION_MODULES.find((m) => m.match(location.pathname));

  // Alerts for the CURRENT module only — cleared out whenever the active
  // module changes, so switching from RFQ to another module's page never
  // leaves stale RFQ badges showing.
  const [notifItems, setNotifItems] = useState([]);

  // NOTE: assumes the login response now includes `name` alongside
  // email/userId in whatever gets stored under localStorage "user". If
  // login hasn't been updated to include it yet, this will just fall
  // back to blank until the user sets one from Settings below (which
  // does keep localStorage in sync going forward).
  const [user, setUser] = useState({
    email: "",
    userId: "",
    name: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Editable "Name" in Settings — separate from the password state above.
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    jobFunction: "",
    department: "",
    address: "",
    phone: "",
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false); // Save stays disabled until the real details are loaded
  const profileReqRef = useRef(0); // ignores a late response if Settings is reopened
  const [nameError, setNameError] = useState("");
  const [nameSuccess, setNameSuccess] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);

      // The saved details can be edited elsewhere (e.g. from Create User),
      // so refresh the name shown in the avatar/dropdown from the server.
      if (parsed.userId) {
        fetchOwnProfile(parsed.userId)
          .then(applyProfileToUser)
          .catch((err) => console.error("SYNC PROFILE ERROR:", err));
      }
    }
  }, []);

  // Fetch the ACTIVE module's notifications for the bell. Runs on mount,
  // re-runs whenever the user navigates into a different module (RFQ vs.
  // whatever gets added next), and refreshes periodically so the badge
  // stays current without a full page reload.
  useEffect(() => {
    // Not on a page that owns a notification bell (e.g. a module with no
    // entry in NOTIFICATION_MODULES yet) — nothing to show.
    if (!activeNotifModule) {
      setNotifItems([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const items = await activeNotifModule.fetchItems();
        if (!cancelled) setNotifItems(items);
      } catch (err) {
        console.error(`Failed to load ${activeNotifModule.key} notifications:`, err);
      }
    };

    load();
    const interval = setInterval(load, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeNotifModule]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    window.location.href = "/login";
  };

  const handleHelpDesk = () => {
    navigate("/cable/help-desk");
  };

  const handleHome = () => {
    navigate("/home");
  };

  // Loads this account's saved details. Tries GET /user/:userId first; if
  // that route isn't available on the running backend, falls back to the
  // existing GET /user/list (which the Create User table already uses) and
  // picks this account out of it.
  const fetchOwnProfile = async (userId) => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(updateUserUrl(userId), { headers });
      if (res.data && typeof res.data === "object" && res.data.userId) return res.data;
    } catch (err) {
      // fall through to the list endpoint
    }
    const res = await axios.get(`${MASTER_API}/user/list`, { headers });
    const match = (res.data?.users || []).find((u) => u.userId === userId);
    if (!match) throw new Error("Your account wasn't found.");
    return match;
  };

  // Writes the server's saved details into `user` state + localStorage so
  // the avatar/dropdown name never goes stale.
  function applyProfileToUser(p) {
    let stored = {};
    try {
      stored = JSON.parse(localStorage.getItem("user")) || {};
    } catch (e) {
      stored = {};
    }
    const next = {
      ...stored,
      firstName: p.firstName || "",
      lastName: p.lastName || "",
      jobFunction: p.jobFunction || "",
      department: p.department || "",
      address: p.address || "",
      phone: p.phone || "",
      name:
        [p.firstName, p.lastName].filter(Boolean).join(" ") ||
        p.name ||
        stored.name ||
        "",
    };
    localStorage.setItem("user", JSON.stringify(next));
    setUser(next);
  }

  const loadOwnProfile = async () => {
    if (!user.userId) return;
    const requestId = ++profileReqRef.current;
    setProfileLoaded(false);
    setProfileLoading(true);
    try {
      const p = await fetchOwnProfile(user.userId);
      if (requestId !== profileReqRef.current) return;
      setProfileForm({
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        jobFunction: p.jobFunction || "",
        department: p.department || "",
        address: p.address || "",
        phone: p.phone || "",
      });
      applyProfileToUser(p);
      setProfileLoaded(true);
    } catch (err) {
      if (requestId !== profileReqRef.current) return;
      console.error("LOAD PROFILE ERROR:", err);
      setNameError(
        err?.response?.data?.message ||
          "Couldn't load your saved details, so saving is disabled. Close and reopen Settings to retry."
      );
    } finally {
      if (requestId === profileReqRef.current) setProfileLoading(false);
    }
  };

  const handleProfileFieldChange = (field, value) =>
    setProfileForm((prev) => ({ ...prev, [field]: value }));

  const openSettings = () => {
    setShowProfile(false);
    setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordError("");
    setPasswordSuccess("");
    // Show what we already have right away (splitting the combined `name`
    // for older stored users), then load the real saved details — whatever
    // was entered in Create User — from the server.
    const [fallbackFirst, ...fallbackRest] = (user.name || "").trim().split(/\s+/).filter(Boolean);
    setProfileForm({
      firstName: user.firstName ?? fallbackFirst ?? "",
      lastName: user.lastName ?? fallbackRest.join(" "),
      jobFunction: user.jobFunction ?? "",
      department: user.department ?? "",
      address: user.address ?? "",
      phone: user.phone ?? "",
    });
    setNameError("");
    setNameSuccess("");
    loadOwnProfile();
    setShowSettings(true);
  };

  const closeSettings = () => setShowSettings(false);

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    const { oldPassword, newPassword, confirmPassword } = passwordForm;

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation don't match.");
      return;
    }
    if (newPassword === oldPassword) {
      setPasswordError("New password must be different from the old one.");
      return;
    }

    const token = localStorage.getItem("token");

    setChangingPassword(true);
    try {
      // The backend re-verifies oldPassword server-side against the
      // stored hash before writing the new one — this isn't just a UI
      // check, so a wrong current password is always rejected there too.
      await axios.post(
        CHANGE_PASSWORD_URL,
        { oldPassword, newPassword },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      setPasswordSuccess("Password updated successfully.");
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordError(
        err?.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setChangingPassword(false);
    }
  };

  // Saves the editable profile details (same fields as Create User). userId
  // and email are never sent — they're fixed for the account.
  const handleSaveName = async () => {
    setNameError("");
    setNameSuccess("");

    if (!user.userId) {
      setNameError("Missing user ID — try logging in again.");
      return;
    }

    const f = Object.fromEntries(
      Object.entries(profileForm).map(([k, v]) => [k, (v || "").trim()])
    );

    if (!f.firstName) {
      setNameError("First name is required.");
      return;
    }
    if (!f.lastName) {
      setNameError("Last name is required.");
      return;
    }
    if (f.phone && !/^[+()\-\s\d]{6,20}$/.test(f.phone)) {
      setNameError("Phone number can only contain digits, spaces, +, - and ( ).");
      return;
    }

    const token = localStorage.getItem("token");

    setSavingName(true);
    try {
      await axios.patch(updateUserUrl(user.userId), f, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      // Keep localStorage (and the dropdown/avatar, which read from
      // `user` state) in sync immediately, without requiring re-login.
      const updatedUser = { ...user, ...f, name: `${f.firstName} ${f.lastName}` };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setNameSuccess("Details updated.");
    } catch (err) {
      setNameError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update details. Please try again."
      );
    } finally {
      setSavingName(false);
    }
  };

  const styles = {
    navbar: {
      height: "72px",
      width: "100%",
      background: "#ffffff",
      borderBottom: "1px solid #e5e7eb",

      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",

      padding: "0 28px",

      position: "fixed",
      top: 0,
      left: 0,
      right: 0,

      zIndex: 10,
    },

    /* LEFT SIDE */
    leftSection: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
    },

    logo: {
      width: "180px",
      objectFit: "contain",
    },

    toggleIcon: {
      fontSize: "21px",
      color: "#4b5563",
      cursor: "pointer",
    },

    /* CENTER */
    industryContainer: {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",

      fontSize: "24px",
      fontWeight: "700",
      color: "#111827",
    },

    /* RIGHT */
    iconContainer: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
    },

    notificationWrapper: {
      position: "relative",
      cursor: "pointer",
    },

    notificationBadge: {
      minWidth: "16px",
      height: "16px",
      padding: "0 4px",
      borderRadius: "9px",
      background: "#ef4444",
      color: "#ffffff",
      fontSize: "10px",
      fontWeight: "700",
      lineHeight: "16px",
      textAlign: "center",

      position: "absolute",
      top: "-6px",
      right: "-8px",
    },

    notifModalBackdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      zIndex: 4000,
    },

    notifModalCard: {
      background: "#ffffff",
      borderRadius: "14px",
      width: "100%",
      maxWidth: "760px",
      maxHeight: "85vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
    },

    notifModalHeader: {
      padding: "18px 24px",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },

    notifModalTitle: {
      fontWeight: "700",
      fontSize: "18px",
      color: "#b91c1c",
    },

    notifModalBody: {
      padding: "8px 0",
      overflowY: "auto",
      flex: 1,
    },

    notifItem: {
      padding: "14px 24px",
      cursor: "pointer",
      fontSize: "14px",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
    },

    notifEmpty: {
      padding: "40px 16px",
      fontSize: "13px",
      color: "#94a3b8",
      textAlign: "center",
    },

    icon: {
      fontSize: "20px",
      color: "#374151",
      cursor: "pointer",
    },

    helpDeskWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },

    profileContainer: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      position: "relative",
    },

    profileText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    },

    userNameTop: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#111827",
      lineHeight: "16px",
      textTransform: "uppercase",
    },

    userRole: {
      fontSize: "12px",
      color: "#6b7280",
      marginTop: "2px",
    },

    avatar: {
      width: "42px",
      height: "42px",
      borderRadius: "50%",
      background: "#031b4e",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      color: "#ffffff",
      fontWeight: "700",
      fontSize: "14px",

      cursor: "pointer",
    },
    centerLogoContainer: {
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},

centerLogo: {
  height: "45px",
  objectFit: "contain",
},

    /* DROPDOWN */
    dropdown: {
      position: "absolute",
      top: "60px",
      right: "0",

      background: "#ffffff",
      border: "1px solid #e5e7eb",

      borderRadius: "12px",

      boxShadow: "0 10px 25px rgba(0,0,0,0.08)",

      padding: "16px",
      width: "240px",

      zIndex: 3000,
    },

    userInfo: {
      marginBottom: "10px",
    },

    userName: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#111827",
    },

    userIdSmall: {
      fontSize: "12px",
      color: "#6b7280",
      fontFamily: "monospace",
      marginTop: "2px",
    },

    userEmail: {
      fontSize: "13px",
      color: "#6b7280",
      marginTop: "4px",
    },

    dropdownIcons: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "12px",
      color: "#374151",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
    },

    logoutBtn: {
      background: "#0d6efd",
      color: "#fff",

      border: "none",

      padding: "10px 14px",

      borderRadius: "8px",

      cursor: "pointer",

      fontSize: "14px",
      fontWeight: "600",

      display: "flex",
      alignItems: "center",
      gap: "8px",

      width: "100%",
      justifyContent: "center",
    },

    /* SETTINGS MODAL */
    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 4000,
    },

    modalCard: {
      background: "#ffffff",
      borderRadius: "14px",
      padding: "28px",
      width: "760px",
      maxWidth: "94vw",
      maxHeight: "92vh",
      overflowY: "auto",
      boxSizing: "border-box",
      boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
      position: "relative",
    },

    // Two-column field layout so the wider modal needs far less scrolling.
    modalGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      columnGap: "16px",
    },
    modalGrid3: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      columnGap: "16px",
    },

    modalCloseBtn: {
      position: "absolute",
      top: "18px",
      right: "18px",
      border: "none",
      background: "none",
      color: "#6b7280",
      cursor: "pointer",
      fontSize: "16px",
    },

    modalTitle: {
      fontSize: "19px",
      fontWeight: "700",
      color: "#111827",
      marginBottom: "4px",
    },

    modalSubtitle: {
      fontSize: "13px",
      color: "#6b7280",
      marginBottom: "18px",
    },

    modalReadonlyRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px solid #f1f5f9",
      fontSize: "13.5px",
    },

    modalReadonlyLabel: {
      color: "#6b7280",
      fontWeight: "600",
    },

    modalReadonlyValue: {
      color: "#111827",
      fontFamily: "monospace",
    },

    modalNote: {
      fontSize: "11.5px",
      color: "#92400e",
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderRadius: "7px",
      padding: "8px 10px",
      marginTop: "8px",
      lineHeight: "1.5",
    },

    modalSectionTitle: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#111827",
      margin: "20px 0 12px",
    },

    modalInputGroup: {
      marginBottom: "12px",
    },

    modalLabel: {
      display: "block",
      fontSize: "12.5px",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "5px",
    },

    modalInput: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      fontSize: "13.5px",
      outline: "none",
      background: "#f9fafb",
      boxSizing: "border-box",
    },

    modalError: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#b91c1c",
      fontSize: "12.5px",
      padding: "8px 10px",
      borderRadius: "7px",
      marginBottom: "12px",
    },

    modalSuccess: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#15803d",
      fontSize: "12.5px",
      padding: "8px 10px",
      borderRadius: "7px",
      marginBottom: "12px",
    },

    modalSubmitBtn: {
      width: "100%",
      padding: "11px",
      background: "#0d6efd",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      fontSize: "13.5px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "6px",
    },

    modalSubmitBtnSecondary: {
      width: "100%",
      padding: "11px",
      background: "#fff",
      color: "#0d6efd",
      border: "1px solid #0d6efd",
      borderRadius: "8px",
      fontSize: "13.5px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "6px",
    },
  };

  return (
    <div style={styles.navbar}>
      {/* LEFT */}
      <div style={styles.leftSection}>
        {/* LOGO */}
        <img
          src={logo}
          alt="CableNXT"
          style={styles.logo}
        />

        {/* HAMBURGER */}
        <FaBars
          style={styles.toggleIcon}
          onClick={toggleSidebar}
          title="Toggle Sidebar"
        />

        {/* HOME */}
        <FaHome
          style={styles.toggleIcon}
          onClick={handleHome}
          title="Home"
        />
      </div>

      {/* CENTER */}
   {/* CENTER */}
<div style={styles.centerLogoContainer}>
  <img
    src={sbeeLogo}
    alt="SBEE Logo"
    style={styles.centerLogo}
  />
</div>

      {/* RIGHT */}
      <div style={styles.iconContainer}>
        {/* NOTIFICATION — only rendered on a page that owns a bell
            (see NOTIFICATION_MODULES above). Swap this for `<div style={{visibility: activeNotifModule ? 'visible' : 'hidden'}}>...` instead
            if you'd rather keep the icon's spot reserved on every page. */}
        {activeNotifModule && (
          <div
            style={styles.notificationWrapper}
            onClick={() =>
              navigate(activeNotifModule.clickPath, { state: activeNotifModule.clickState })
            }
            title={activeNotifModule.label}
          >
            <FaBell style={styles.icon} />
            {notifItems.length > 0 && (
              <div style={styles.notificationBadge}>
                {notifItems.length > 99 ? "99+" : notifItems.length}
              </div>
            )}
          </div>
        )}

        {/* HELP DESK */}
        <div
          style={styles.helpDeskWrapper}
          onClick={handleHelpDesk}
          title="Help Desk"
        >
          <FaHeadset style={styles.icon} />
        </div>

        {/* PROFILE */}
        <div style={styles.profileContainer}>
          {/* AVATAR */}
          <div
            style={styles.avatar}
            onClick={() => setShowProfile(!showProfile)}
          >
            {(user.name || user.loggedInAs || user.userId || user.email)
              ? (user.name || user.loggedInAs || user.userId || user.email).charAt(0).toUpperCase()
              : "?"}
          </div>

          {/* DROPDOWN */}
          {showProfile && (
            <div style={styles.dropdown}>
              <div style={styles.userInfo}>
                <div style={styles.userName}>
                  {user.name || user.loggedInAs || user.userId || "—"}
                </div>

                {/* Show the User ID separately whenever we're already
                    leading with a name or a "logged in as" label above,
                    so people can still see the login handle at a glance. */}
                {(user.name || user.loggedInAs) && user.userId && (
                  <div style={styles.userIdSmall}>
                    User ID: {user.userId}
                  </div>
                )}

                <div style={styles.userEmail}>
                  {user.email}
                </div>
              </div>

              <hr
                style={{
                  margin: "12px 0",
                  borderColor: "#e5e7eb",
                }}
              />

              <div style={styles.dropdownIcons} onClick={openSettings}>
                <FaCog />
                Settings
              </div>

              <button
                onClick={handleLogout}
                style={styles.logoutBtn}
              >
                <FaSignOutAlt />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SETTINGS MODAL */}
      {showSettings && (
        <div style={styles.modalOverlay} onClick={closeSettings}>
          <div
            style={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.modalCloseBtn}
              onClick={closeSettings}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div style={styles.modalTitle}>Account Settings</div>
            <div style={styles.modalSubtitle}>
              Your account details and password
            </div>

            <div style={styles.modalReadonlyRow}>
              <span style={styles.modalReadonlyLabel}>User ID</span>
              <span style={styles.modalReadonlyValue}>{user.userId || "—"}</span>
            </div>
            <div style={styles.modalReadonlyRow}>
              <span style={styles.modalReadonlyLabel}>Email</span>
              <span style={styles.modalReadonlyValue}>{user.email || "—"}</span>
            </div>
            {Array.isArray(user.otherAccountsWithThisEmail) &&
              user.otherAccountsWithThisEmail.length > 0 && (
                <div style={styles.modalNote}>
                  {user.otherAccountsWithThisEmail.length} other separate
                  account{user.otherAccountsWithThisEmail.length > 1 ? "s" : ""} also
                  use{user.otherAccountsWithThisEmail.length > 1 ? "" : "s"} this email
                  ({user.otherAccountsWithThisEmail.join(", ")}) — each has its own
                  password and changing yours won't affect them.
                </div>
              )}

            <div style={styles.modalSectionTitle}>Profile details</div>

            {nameError && <div style={styles.modalError}>{nameError}</div>}
            {nameSuccess && <div style={styles.modalSuccess}>{nameSuccess}</div>}

            {profileLoading && (
              <div style={styles.modalNote}>Loading your details…</div>
            )}

            <div style={styles.modalGrid}>
            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>First name *</label>
              <input
                type="text"
                value={profileForm.firstName}
                onChange={(e) => handleProfileFieldChange("firstName", e.target.value)}
                name="profile-firstName"
                autoComplete="off"
                style={styles.modalInput}
                placeholder="e.g. Jane"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Last name *</label>
              <input
                type="text"
                value={profileForm.lastName}
                onChange={(e) => handleProfileFieldChange("lastName", e.target.value)}
                name="profile-lastName"
                autoComplete="off"
                style={styles.modalInput}
                placeholder="e.g. Smith"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Function</label>
              <input
                type="text"
                value={profileForm.jobFunction}
                onChange={(e) => handleProfileFieldChange("jobFunction", e.target.value)}
                name="profile-jobFunction"
                autoComplete="off"
                style={styles.modalInput}
                placeholder="e.g. Quality Engineer"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Department</label>
              <input
                type="text"
                value={profileForm.department}
                onChange={(e) => handleProfileFieldChange("department", e.target.value)}
                name="profile-department"
                autoComplete="off"
                style={styles.modalInput}
                placeholder="e.g. Quality"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Address</label>
              <textarea
                rows={2}
                value={profileForm.address}
                onChange={(e) => handleProfileFieldChange("address", e.target.value)}
                name="profile-address"
                autoComplete="off"
                style={{ ...styles.modalInput, resize: "vertical", fontFamily: "inherit" }}
                placeholder="Street, city, state, postal code"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Phone number</label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => handleProfileFieldChange("phone", e.target.value)}
                name="profile-phone"
                autoComplete="off"
                style={styles.modalInput}
                placeholder="e.g. +91 98765 43210"
              />
            </div>

            </div>

            <button
              style={{
                ...styles.modalSubmitBtnSecondary,
                opacity: savingName || !profileLoaded ? 0.7 : 1,
                cursor: savingName || !profileLoaded ? "not-allowed" : "pointer",
              }}
              onClick={handleSaveName}
              disabled={savingName || !profileLoaded}
            >
              {savingName ? "Saving…" : "Save Details"}
            </button>

            <div style={styles.modalSectionTitle}>Change Password</div>

            {passwordError && (
              <div style={styles.modalError}>{passwordError}</div>
            )}
            {passwordSuccess && (
              <div style={styles.modalSuccess}>{passwordSuccess}</div>
            )}

            <div style={styles.modalGrid3}>
            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Current password</label>
              <input
                type="password"
                name="account-current-password"
                autoComplete="off"
                value={passwordForm.oldPassword}
                onChange={(e) =>
                  handlePasswordFieldChange("oldPassword", e.target.value)
                }
                style={styles.modalInput}
                placeholder="Enter your current password"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>New password</label>
              <input
                type="password"
                name="account-new-password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  handlePasswordFieldChange("newPassword", e.target.value)
                }
                style={styles.modalInput}
                placeholder="At least 8 characters"
              />
            </div>

            <div style={styles.modalInputGroup}>
              <label style={styles.modalLabel}>Confirm new password</label>
              <input
                type="password"
                name="account-confirm-password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  handlePasswordFieldChange("confirmPassword", e.target.value)
                }
                style={styles.modalInput}
                placeholder="Re-enter new password"
              />
            </div>

            </div>

            <button
              style={{
                ...styles.modalSubmitBtn,
                opacity: changingPassword ? 0.7 : 1,
                cursor: changingPassword ? "not-allowed" : "pointer",
              }}
              onClick={handleChangePassword}
              disabled={changingPassword}
            >
              {changingPassword ? "Updating…" : "Update Password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

Navbar.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
};

export default Navbar;